import React, { useEffect, useState, useCallback, memo, useMemo, useRef } from 'react';
import { connectToSignalR, getConnectedAnalyzers, disconnectSignalR } from '../../../signalR';
import { SysI_Analizadores } from '../../../types/types.analizadores';
import { analizer } from '../../../images/icons';
import './InfoAnalyzers.scss';
import { useApiConfiguration } from '../../../api/api.configuration';
import { modalManager, ModalRenderer } from './ModalManager';
import { useAppSelector } from '../../../contexts/hooks';
import { shallowEqual } from 'react-redux';

// ─── Componentes fuera del cuerpo del componente principal para que memo() funcione ───

const AnalyzerCard = memo<{
  analyzer: SysI_Analizadores;
  index: number;
  isConnected: boolean;
  onAnalyzerClick: (analyzer: SysI_Analizadores) => void;
  onKeyPress: (event: React.KeyboardEvent, analyzer: SysI_Analizadores) => void;
}>(
  ({ analyzer, index, isConnected, onAnalyzerClick, onKeyPress }) => {
    const handleClick = useCallback(() => {
      onAnalyzerClick(analyzer);
    }, [analyzer, onAnalyzerClick]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        onKeyPress(e, analyzer);
      },
      [analyzer, onKeyPress]
    );

    return (
      <div className={`modern-analyzer-card ${isConnected ? 'connected' : 'disconnected'}`} onClick={handleClick} onKeyDown={handleKeyDown} tabIndex={0} role='button' aria-label={`Analizador ${analyzer.Az_Nombre} - ${isConnected ? 'Conectado' : 'Desconectado'}`} style={{ animationDelay: `${index * 0.1}s` }}>
        {' '}
        <div className='card-content'>
          <div className='analyzer-header'>
            <div className={`status-circle ${isConnected ? 'online' : 'offline'}`}></div>
            <div className='analyzer-icon-container'>
              <img src={analizer} alt={analyzer.Az_Nombre} className='analyzer-icon' />
            </div>
            <div className='analyzer-info'>
              <h3 className='analyzer-name'>{analyzer.Az_Nombre}</h3>
              <p className='analyzer-description'>{analyzer.Az_Descripcion}</p>
            </div>
          </div>
          <div className='analyzer-footer'>
            <div className='connection-status'>
              <span className={`status-text ${isConnected ? 'online' : 'offline'}`}>{isConnected ? 'Conectado' : 'Desconectado'}</span>
            </div>
            <div className='action-buttons'>
              {' '}
              <button
                className='help-button'
                onClick={(e) => {
                  e.stopPropagation();
                }}
                title='Ayuda'
                aria-label={`Ayuda para ${analyzer.Az_Nombre}`}>
                <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                  <path d='M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A2,2 0 0,1 14,8A2,2 0 0,1 12,10A2,2 0 0,1 10,8A2,2 0 0,1 12,6M12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17A2,2 0 0,1 10,15A2,2 0 0,1 12,13Z' />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.analyzer.Az_Id === nextProps.analyzer.Az_Id && prevProps.isConnected === nextProps.isConnected && prevProps.index === nextProps.index;
  }
);

const AnalyzersGrid = memo<{
  analyzers: SysI_Analizadores[];
  connectedAnalyzersSet: Set<string>;
  onAnalyzerClick: (analyzer: SysI_Analizadores) => void;
  onKeyPress: (event: React.KeyboardEvent, analyzer: SysI_Analizadores) => void;
}>(
  ({ analyzers, connectedAnalyzersSet, onAnalyzerClick, onKeyPress }) => {
    return (
      <div className='analyzers-grid'>
        {analyzers.map((analyzer, index) => {
          const isConnected = connectedAnalyzersSet.has(analyzer.Az_IdentificadorUnico);
          return <AnalyzerCard key={analyzer.Az_Id} analyzer={analyzer} index={index} isConnected={isConnected} onAnalyzerClick={onAnalyzerClick} onKeyPress={onKeyPress} />;
        })}
      </div>
    );
  }
);
interface InfoAnalyzersProps {
  orientation: 'horizontal' | 'vertical';
  handleClick?: (analyzer: SysI_Analizadores) => void;
}

const InfoAnalyzersComponent: React.FC<InfoAnalyzersProps> = ({ orientation, handleClick }) => {
  const [analyzers, setAnalyzers] = useState<SysI_Analizadores[]>([]);
  const [connectedAnalyzers, setConnectedAnalyzers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const Prm_Ord_ViewOtherLocation = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_ViewOtherLocation);
  const ModuleLocation: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleLocation')[0].Cgn_Resultado, shallowEqual) || 'false');
  const IdSede = useAppSelector((x) => x.userInfo.Location);
  const { getAnalizadores } = useApiConfiguration();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Optimize connected analyzers lookup using Set for O(1) lookup
  const connectedAnalyzersSet = useMemo(() => {
    return new Set(connectedAnalyzers.map((a) => a.AnalyzerId));
  }, [connectedAnalyzers]);

  // Función estable para refrescar la lista de analizadores conectados desde el servidor
  const refreshConnectedAnalyzers = useCallback(async () => {
    try {
      const connected = await getConnectedAnalyzers();
      setConnectedAnalyzers(connected.map((a: { AnalyzerId: string }) => a));
    } catch (error) {
      console.error('Error al obtener analizadores conectados:', error);
    }
  }, []);

  // Callback para eventos SignalR: programa un refresco desde el servidor con debounce
  // para evitar múltiples llamadas si llegan varios eventos seguidos
  const handleAnalyzerStatusUpdate = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = setTimeout(() => {
      refreshConnectedAnalyzers();
    }, 300);
  }, [refreshConnectedAnalyzers]);

  useEffect(() => {
    const initializeConnection = async () => {
      try {
        await connectToSignalR(handleAnalyzerStatusUpdate);
        
        await refreshConnectedAnalyzers();

        fetchAnalyzers();
      } catch (error) {
        console.error('❌ Error al inicializar SignalR:', error);
      }
    };

    initializeConnection();

    // Polling de respaldo cada 10s para capturar cambios que los eventos no entregaron
    const pollInterval = setInterval(refreshConnectedAnalyzers, 10000);

    return () => {
      disconnectSignalR();
      clearInterval(pollInterval);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const fetchAnalyzers = async () => {
    try {
      setIsLoading(true);
      getAnalizadores().then((data) => {   
        setAnalyzers(data.filter((a) => {
          // Primero verificar que esté activo
          if (!a.Az_Estado) return false;
          
          // Si ModuleLocation está activo y NO tiene permiso para ver otras sedes
          if (ModuleLocation && !Prm_Ord_ViewOtherLocation) {
            // Solo mostrar analizadores de la sede actual
            return a.Az_Sd_Id === IdSede;
          }
          
          // Si tiene permiso para ver otras sedes o ModuleLocation está inactivo, mostrar todos
          return true;
        }));
        setIsLoading(false);
      });
    } catch (error) {
      console.error('❌ Error al obtener analizadores:', error);
      setIsLoading(false);
    }
  };

  const handleAnalyzerClick = useCallback(
    (analyzer: SysI_Analizadores) => {
      if (handleClick) {
        handleClick(analyzer);
      } else {
        modalManager.openModal(analyzer);
      }
    },
    [handleClick]
  );

  // Add keyboard navigation
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent, analyzer: SysI_Analizadores) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleAnalyzerClick(analyzer);
      }
    },
    [handleAnalyzerClick]
  );
  if (isLoading) {
    return (
      <div className='analyzers-loading'>
        <div className='loading-spinner'>Cargando analizadores...</div>
      </div>
    );
  }

  if (analyzers.length === 0) {
    return (
      <div className='analyzers-empty'>
        <h3>No hay analizadores disponibles</h3>
        <p>No se encontraron analizadores configurados en el sistema.</p>
      </div>
    );
  }
  return (
    <div className={`analyzers-container ${orientation}`}>
      <AnalyzersGrid analyzers={analyzers} connectedAnalyzersSet={connectedAnalyzersSet} onAnalyzerClick={handleAnalyzerClick} onKeyPress={handleKeyPress} />
      <ModalRenderer />
    </div>
  );
};

const InfoAnalyzers = memo(InfoAnalyzersComponent);

export default InfoAnalyzers;
